import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'
import { IS_GBSTORAGE, PLAN_ACTIVE } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getSubscriptionPeriod } from '@disco/state-management/selectors/subscription'
import {
  getBusinessUsers,
  getCorePlan,
  getTracksLimits,
  getUsersLimits,
  getTracksQuota,
  getUsersQuota,
} from '@disco/state-management/selectors/user'
import LitePlanUpdateModal from 'components/modals/lite-plan-update-modal'
import SeatsAndTracks from './SeatsAndTracks'
import type { BusinessStats, PrivateMetadata } from 'types/business'

const SeatsAndTracksContainer = (): null | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { number_of_users, total_track_count } = useSelector(
    state => state.user.business?.stats || ({} as BusinessStats),
  )
  const usersQuota = useSelector(getUsersQuota)
  const tracksQuota = useSelector(getTracksQuota)

  const businessUsers = useSelector(getBusinessUsers)
  const corePlan = useSelector(getCorePlan)
  // TODO: add a feature and check through useFeatureEnabled()
  const showLitePlanUpdateModal = corePlan.id === '2020-lite'
  const planActive = useFeatureEnabled(PLAN_ACTIVE)
  const isGbstorage = useFeatureEnabled(IS_GBSTORAGE)
  const disabledUpdatePaymentDetails = useSelector(
    state => !state.user?.permissions?.includes('can_change_business_settings'),
  )
  const subscription = useSelector(state => state.subscription)
  const usersLimits = useSelector(getUsersLimits)
  const tracksLimits = useSelector(getTracksLimits)
  useEffect(() => {
    if (!businessUsers.loading && !businessUsers.loaded) {
      dispatch(loadBusinessUsers())
    }

    dispatch(loadBillingProviderComponents())
  }, [])

  if (showLitePlanUpdateModal) {
    return (
      <LitePlanUpdateModal
        enterprise_track_allowance={tracksQuota}
        enterprise_user_allowance={usersQuota}
      />
    )
  }

  if (
    planActive &&
    !isGbstorage &&
    !disabledUpdatePaymentDetails &&
    !subscription.error &&
    subscription.loaded &&
    !(usersLimits.updating || tracksLimits.updating)
  ) {
    if (corePlan) {
      return (
        <SeatsAndTracks
          enterprise_track_allowance={tracksQuota}
          enterprise_user_allowance={usersQuota}
          number_of_users={businessUsers.users.length || number_of_users}
          total_track_count={total_track_count}
          currentBusinessPlan={corePlan}
          onClose={() => navigate('/settings/billing')}
          usersLoading={businessUsers.loading}
        />
      )
    } else {
      return null
    }
  } else {
    navigate('/settings/billing', { replace: true })
    return null
  }
}

export default SeatsAndTracksContainer
